import { api } from '../../auth/authStore';
import toast from 'react-hot-toast';

export type ExportFormat = 'pdf' | 'excel' | 'csv' | 'json' | 'png';

interface ExportOptions {
  reportType: string;
  format: ExportFormat;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  filename?: string;
  chartIndex?: number; // For PNG chart exports
}

interface ReportData {
  title: string;
  data: any[];
  summary: Record<string, any>;
  charts?: any[];
}

export class ReportExporter {
  /**
   * Export report in specified format
   */
  static async exportReport(options: ExportOptions): Promise<void> {
    try {
      toast(`Preparing ${options.format.toUpperCase()} export...`, { icon: '📊' });

      if (options.format === 'png') {
        await this.exportChartAsPNG(options);
        return;
      }

      if (options.format === 'json') {
        await this.exportAsJSON(options);
        return;
      }

      // For PDF, Excel, CSV - use backend API
      await this.exportViaAPI(options);

    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
      throw error;
    }
  }

  /**
   * Export via backend API (PDF, Excel, CSV)
   */
  private static async exportViaAPI(options: ExportOptions): Promise<void> {
    const { reportType, format, dateRange } = options;

    // Map frontend reportType to backend endpoint
    const reportTypeMapping: Record<string, string> = {
      'task-performance': 'tasks',
      'financial': 'financial',
      'time-tracking': 'timetracking',
      'attendance': 'attendance',
      'client-activity': 'clients'
    };

    // Check if report type is supported for export
    const unsupportedReportTypes = ['payroll', 'comprehensive'];
    if (unsupportedReportTypes.includes(reportType)) {
      toast.error(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report export is not yet implemented`);
      return;
    }

    const backendReportType = reportTypeMapping[reportType] || reportType;

    // Build params object
    const params: any = {
      format: format // Keep format as-is (pdf, excel, csv) - backend expects 'excel' not 'xlsx'
    };

    // Only add date range if provided (undefined means export all data)
    if (dateRange) {
      if (dateRange.startDate) {
        params.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        params.endDate = dateRange.endDate;
      }
    }

    // Use the specific report endpoint (same pattern for all reports)
    const endpoint = `/firm-admin/reports/${backendReportType}`;

    const response = await api.get(endpoint, {
      params,
      responseType: format === 'json' ? 'json' : 'blob'
    });

    // Handle different response types
    if (format === 'json') {
      // For JSON format, handle the structured response
      if (response.data?.success && response.data?.data) {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: 'application/json'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = options.filename || `${reportType}-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('JSON report downloaded successfully');
        return;
      } else {
        throw new Error('Invalid response from report generation API');
      }
    } else {
      // Handle blob response for direct download endpoints
      const mimeTypes = {
        pdf: 'application/pdf',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv'
      };

      const extensions = {
        pdf: 'pdf',
        excel: 'xlsx',
        csv: 'csv'
      };

      const blob = new Blob([response.data], {
        type: mimeTypes[format as keyof typeof mimeTypes] || 'application/octet-stream'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = options.filename ||
        `${reportType}-report-${new Date().toISOString().split('T')[0]}.${extensions[format as keyof typeof extensions]}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    toast.success(`Report exported as ${format.toUpperCase()} successfully!`);
  }

  /**
   * Export report data as JSON
   */
  private static async exportAsJSON(options: ExportOptions): Promise<void> {
    const { reportType, dateRange } = options;

    // Fetch report data
    const now = new Date();
    const defaultStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const defaultEndDate = now.toISOString().split('T')[0];

    const response = await api.get(`/firm-admin/reports/view/${reportType}`, {
      params: {
        startDate: dateRange?.startDate || defaultStartDate,
        endDate: dateRange?.endDate || defaultEndDate
      }
    });

    if (!response.data.success) {
      throw new Error('Failed to fetch report data');
    }

    const jsonData = JSON.stringify(response.data.data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = options.filename || 
      `${reportType}-report-${new Date().toISOString().split('T')[0]}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Report exported as JSON successfully!');
  }

  /**
   * Export chart as PNG (placeholder - would need chart library integration)
   */
  private static async exportChartAsPNG(options: ExportOptions): Promise<void> {
    // This would require integration with a chart library like Chart.js or Recharts
    // For now, show a placeholder message
    toast('Chart export feature coming soon', { icon: 'ℹ️' });
    
    // Future implementation would:
    // 1. Get chart canvas element
    // 2. Convert to blob
    // 3. Download as PNG
    
    /*
    const canvas = document.querySelector('canvas'); // Get chart canvas
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = options.filename || 
            `${options.reportType}-chart-${options.chartIndex || 0}-${new Date().toISOString().split('T')[0]}.png`;
          link.click();
          window.URL.revokeObjectURL(url);
          toast.success('Chart exported as PNG successfully!');
        }
      });
    }
    */
  }

  /**
   * Export data table as CSV
   */
  static exportTableAsCSV(data: any[], filename: string): void {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Data exported as CSV successfully!');
  }

  /**
   * Export data table as Excel (simplified version)
   */
  static exportTableAsExcel(data: any[], filename: string): void {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create a simple HTML table that Excel can import
    const headers = Object.keys(data[0]);
    const htmlContent = `
      <table>
        <thead>
          <tr>
            ${headers.map(header => `<th>${header}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data.map(row => 
            `<tr>
              ${headers.map(header => `<td>${row[header] || ''}</td>`).join('')}
            </tr>`
          ).join('')}
        </tbody>
      </table>
    `;

    const blob = new Blob([htmlContent], { 
      type: 'application/vnd.ms-excel' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.xls') ? filename : `${filename}.xls`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Data exported as Excel successfully!');
  }

  /**
   * Get available export formats for a report type
   */
  static getAvailableFormats(reportType: string, hasCharts: boolean = false): ExportFormat[] {
    const baseFormats: ExportFormat[] = ['pdf', 'excel', 'csv', 'json'];
    
    if (hasCharts) {
      baseFormats.push('png');
    }

    return baseFormats;
  }

  /**
   * Get format display name
   */
  static getFormatDisplayName(format: ExportFormat): string {
    const displayNames = {
      pdf: 'PDF Document',
      excel: 'Excel Spreadsheet',
      csv: 'CSV File',
      json: 'JSON Data',
      png: 'PNG Image'
    };

    return displayNames[format] || format.toUpperCase();
  }

  /**
   * Get format icon
   */
  static getFormatIcon(format: ExportFormat): string {
    const icons = {
      pdf: '📄',
      excel: '📊',
      csv: '📋',
      json: '🔧',
      png: '🖼️'
    };

    return icons[format] || '📁';
  }
}
